import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import Label from "@/components/form-controls/Label";

type DropSelectInputProps = React.ComponentProps<"input"> & {
    onValueChange?: (value: number) => void;
    onCurrencyChange?: (currency: string) => void;
    error?: string;
    disabled?: boolean;
    labelStyle?: string;
    inputStyle?: string;
    hasError?: boolean;
    hint?: string;
    label: string;
    register: any;
    registerCurrency: any;
}

const DropSelectInput: React.FC<DropSelectInputProps> = ({
    label,
    onValueChange,
    onCurrencyChange,
    error,
    disabled,
    labelStyle,
    hint,
    register,
    registerCurrency,
    ...extra
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(registerCurrency.value || "TZS");
    const currencies = ["TZS", "USD", "EUR", "GBP"];

    useEffect(() => {
        if (registerCurrency.value) {
            setSelectedCurrency(registerCurrency.value);
        }
    }, [registerCurrency.value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/[^\d.]/g, '');
        
        const parts = value.split('.');
        if (parts[1]?.length > 2) {
            parts[1] = parts[1].slice(0, 2);
            value = parts.join('.');
        }

        if (register.onChange) {
            const event = {
                ...e,
                target: {
                    ...e.target,
                    value
                }
            };
            register.onChange(event);
        }
        
        onValueChange?.(Number(value));
    };

    const handleCurrencySelect = (newCurrency: string) => {
        setSelectedCurrency(newCurrency);
        
        const event = {
            target: {
                name: registerCurrency.name,
                value: newCurrency
            }
        };
        registerCurrency.onChange(event);
        
        onCurrencyChange?.(newCurrency);
        
        setIsDropdownOpen(false);
    };

    return (
        <div className="w-full">
            <Label 
                name={register.name} 
                text={label} 
                required={extra.required}
                className={labelStyle}
                hint={hint}
            />

            <div className="relative flex rounded-lg border border-secondary-100">
                <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-1 text-sm px-4 py-2 border-r border-gray-200 text-gray-800"
                >
                    {selectedCurrency}
                    <ChevronDown size={16} />
                </button>

                <input
                    type="text"
                    {...register}
                    onChange={handleChange}
                    className="w-full border-none rounded-md focus:border-primary focus:outline-none focus:ring-primary focus:ring-offset-1"
                    {...extra}
                />

                <input
                    type="hidden"
                    {...registerCurrency}
                    value={selectedCurrency}
                />

                {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-24 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                        {currencies.map((curr) => (
                            <button
                                type="button"
                                key={curr}
                                onClick={() => handleCurrencySelect(curr)}
                                className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-50 ${
                                    curr === selectedCurrency ? 'bg-gray-100' : ''
                                }`}
                            >
                                {curr}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {error && <p className="mt-1 text-xs font-medium text-rose-800 border-rose-800">{error}</p>}
        </div>
    );
};

export default DropSelectInput;